import { useEffect, useState } from 'react';
import { appConfig } from '../config/appConfig';
import { initializeClarity, type ClarityConsentState } from '../lib/clarity';

const CONSENT_STORAGE_KEY = 'democracyOnTheBallot.analyticsConsent';

type AnalyticsConsentChoice = 'necessary' | 'anonymous' | 'full';

const clarityConsentByChoice: Record<Exclude<AnalyticsConsentChoice, 'necessary'>, ClarityConsentState> = {
  anonymous: {
    adStorage: 'denied',
    analyticsStorage: 'denied',
  },
  full: {
    adStorage: 'granted',
    analyticsStorage: 'granted',
  },
};

function normalizeStoredChoice(value: string | null): AnalyticsConsentChoice | null {
  if (value === 'necessary' || value === 'anonymous' || value === 'full') return value;
  if (value === 'denied') return 'necessary';
  if (value === 'granted') return 'full';
  return null;
}

export function AnalyticsConsentBanner() {
  const [choice, setChoice] = useState<AnalyticsConsentChoice | null>(() => {
    if (typeof window === 'undefined') return null;
    return normalizeStoredChoice(window.localStorage.getItem(CONSENT_STORAGE_KEY));
  });

  useEffect(() => {
    if (choice === 'anonymous' || choice === 'full') {
      initializeClarity(clarityConsentByChoice[choice]);
    }
  }, [choice]);

  if (!import.meta.env.PROD || !appConfig.enableClarity || !appConfig.clarityProjectId || choice !== null) {
    return null;
  }

  const saveChoice = (nextChoice: AnalyticsConsentChoice) => {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, nextChoice);
    setChoice(nextChoice);
  };

  return (
    <aside className="analytics-consent-banner" aria-label="Analytics consent">
      <div>
        <strong>Help improve this site?</strong>
        <p>
          Necessary storage remembers your preference. You can also allow Microsoft Clarity to
          collect anonymous, no-cookie usage insights, or allow full analytics cookies for more
          complete heatmaps and session insights.
        </p>
      </div>
      <div className="analytics-consent-actions">
        <button type="button" className="btn btn-ghost" onClick={() => saveChoice('necessary')}>
          Necessary only
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => saveChoice('anonymous')}>
          Anonymous insights
        </button>
        <button type="button" className="btn btn-primary" onClick={() => saveChoice('full')}>
          Full analytics
        </button>
      </div>
    </aside>
  );
}
